import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useAPI } from '../../hooks/useAPI';
import { Button, Stack } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? '');

interface CheckoutFormProps {
  redirectUrl?: string;
  onBeforeSubmit?: Function;
}

const CheckoutForm = ({ redirectUrl, onBeforeSubmit }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      if (onBeforeSubmit && !(await onBeforeSubmit())) {
        setLoading(false);
        return;
      }

      if (!stripe || !elements) {
        setLoading(false);
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${redirectUrl}`,
        },
      });

      if (error) {
        alert(error.message);
      } else {
        alert('Payment Successful');
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('An error occurred during payment processing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
        alignItems: 'flex-end',
      }}>
      <Stack width="100%">
        <PaymentElement />
      </Stack>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        sx={{ fontWeight: '900', textTransform: 'unset' }}
        disabled={!stripe || loading}>
        {loading ? 'Processing...' : 'Pay'}
      </Button>
    </form>
  );
};

interface StripePaymentProps {
  amount?: number;
  redirectUrl?: string;
  onBeforeSubmit?: Function;
}

const StripePayment = ({
  amount,
  redirectUrl,
  onBeforeSubmit,
}: StripePaymentProps) => {
  const { fetchData } = useAPI();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const [currentAmount, setCurrentAmount] = useState<number | undefined>(
    amount,
  );

  useEffect(() => {
    // Reset client secret when amount changes
    if (amount !== currentAmount) {
      setClientSecret(null);
      setCurrentAmount(amount);
    }
  }, [amount, currentAmount]);

  useEffect(() => {
    const fetchClientSecret = async () => {
      if (!amount || amount <= 0) return;

      try {
        const { errors, data } = await fetchData(
          '/create-payment-intent',
          { amount: Math.round(amount * 100) },
          'POST',
        );
        if (!errors && data?.client_secret) {
          setClientSecret(data.client_secret);
        } else {
          console.error('Failed to get client secret:', errors);
        }
      } catch (err) {
        console.error('Error fetching client secret:', err);
      }
    };

    if (isAuthenticated && !clientSecret && amount && amount > 0) {
      fetchClientSecret();
    }
  }, [isAuthenticated, amount, clientSecret, fetchData]);

  if (!clientSecret || !amount || amount <= 0) return null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: clientSecret }}>
      <CheckoutForm redirectUrl={redirectUrl} onBeforeSubmit={onBeforeSubmit} />
    </Elements>
  );
};

export default StripePayment;
